/**
 * Alerts-list panel — the dockable surface listing active (undismissed)
 * SAPIENT alerts with a per-alert Dismiss control, per ADR-029 §6.2 +
 * ADR-024 §2 + the Map <-> Alerts synchronized-selection edge
 * (REQ-UI-051 / ADR-024 §5). REQ-UI-062; storyboard Flow F. The Alert
 * sibling of :func:`detections/detections-panel`.
 *
 * # Data
 *
 * Reads the shared :func:`alerts/alerts-store` buffer (active, newest-
 * first), which :func:`alerts/use-recent-alerts-feed` seeds from the
 * global ``GET /api/query/recent-alerts?active_only=true`` (polled) and
 * :func:`alerts/use-alerts-sse-bridge` tops up with live ``alert`` deltas
 * + cross-session ``alert-dismissed`` removals. The panel mounts the feed
 * so the list populates whenever it's open; the buffer is shared with the
 * (PR5b) Map pin layer so both surfaces show the same set.
 *
 * # Row contract (ADR-029 §6.2)
 *
 * Each row shows a **priority badge** (label + the shared status swatch
 * via :func:`describeAlertPriority`), the **alert type** label, the
 * **description** (truncated, "(no description)" when null), the **source**
 * Producer (``producer_id``, mirroring the Detections row), the **age**
 * (relative time since ``received_at``), and — for operator/admin only —
 * a **Dismiss** button.
 *
 * # Dismiss (operator/admin only)
 *
 * The Dismiss button renders only when the session
 * ``hasAnyRole(["operator","admin"])`` (REQ-AUTH; the BFF enforces this
 * too, but the UI hides the control for non-dismissers per the fail-safe
 * stance, CLAUDE.md §8.1). Clicking it fires :func:`useDismissAlert` and
 * ``stopPropagation``s so it doesn't also select the row; while the
 * mutation is pending the button is disabled and shows "Dismissing…"; a
 * failure surfaces an inline message (a 403 reads as a permission error,
 * not a transient one). On success the alert leaves the list (optimistic
 * local removal + the idempotent cross-session SSE).
 *
 * # Selection (the 2026-06-09 two-slot redesign)
 *
 *   - **Row click -> inspected.** Writes ``{kind: "alert", id}`` to the
 *     ``inspected`` slot ONLY; Entity Details populates via the detail
 *     fetch (PR5a inc 4) and this row highlights. The Map pin ring/camera
 *     are left untouched (the intended divergence — push-to-map is the
 *     Phase-2 "Select on Map" affordance).
 *   - **Map pin-click -> row highlight.** A Map pin click sets BOTH slots,
 *     so the matching row here left-edge-highlights via
 *     :func:`useIsInspected`.
 *   - **Right-click / context-menu key -> "Select on Map".** Each row is
 *     wrapped with :func:`SelectOnMapContextMenu`, whose single item pushes
 *     the alert to the map (both slots + force-recenter). An alert with no
 *     resolvable location (REQ-UI-069) is map-invisible, so the item is
 *     disabled with a "No map location" hint (Phase-2 affordance).
 *
 * Display-only mode (REQ-UI-056) is not yet implemented in the UI and is
 * not gated here — consistent with the Events / Detections / Platforms
 * panels, which also call ``select()`` ungated.
 *
 * Cross-references:
 *   - REQ-UI-062 (Alerts panel); REQ-UI-051 (synchronized selection);
 *     REQ-UI-059 (keyboard nav); REQ-UI-046 (status-colour legibility —
 *     badge label + swatch, not colour alone); REQ-UI-026/027.
 *   - REQ-INGEST-044 (nullable confidence / position).
 *   - ADR-024 §2 / §5; ADR-029 §6.2; ADR-032; storyboard Flow F.
 */
import { useMemo, useRef } from "react";
import {
  useIsInspected,
  useInspectedIdForKind,
  useMapSelectedIdForKind,
  useSelectionStore,
} from "@/selection/selection-store";
import { SelectOnMapContextMenu } from "@/selection/select-on-map-context-menu";
import {
  type PanelVisibilityApi,
  useKeepSelectedRowInView,
} from "@/selection/use-keep-selected-row-in-view";
import { useListFollow } from "@/multi-pane/list-follow-store";
import { ListFollowToggle } from "@/multi-pane/list-follow-toggle";
import { usePausedListRows } from "@/multi-pane/use-paused-list-rows";
import { useSession } from "@/auth/session-context";
import { type AlertRecord, useActiveAlerts } from "@/alerts/alerts-store";
import { useRecentAlertsFeed } from "@/alerts/use-recent-alerts-feed";
import { useDismissAlert } from "@/alerts/use-dismiss-alert";
import {
  ALERT_DISMISSER_ROLES,
  alertTypeLabel,
  describeAlertPriority,
  formatAlertAge,
} from "@/alerts/alert-display";
import { getStatusColor } from "@/map/marker-styling";
import { usePreferences } from "@/preferences/preferences-context";
import { useProducerNames } from "@/lib/use-producer-names";
import { shortId } from "@/lib/short-id";
import { ALERT_DEFAULT_SORT, sortAlertRows } from "@/alerts/alert-sort";

interface AlertsPanelProps {
  /**
   * dockview panel api, threaded by :func:`AlertsDockPanel`. Lets the
   * keep-in-view hook re-centre the selected row when this tab is shown
   * after a Map selection made while it was hidden (REQ-UI-051; PR5b Map
   * half). Optional so the panel renders standalone in tests.
   */
  panelApi?: PanelVisibilityApi;
}

/**
 * Render the Alerts-list panel.
 *
 * Mounted by :file:`@/multi-pane/panel-registry.tsx` against the
 * ``alerts`` :type:`PanelId`. Full-height scrollable region; dockview
 * owns the surrounding chrome.
 */
/** An alert row's stable identity for the pause snapshot. */
const alertRowId = (alert: AlertRecord): string => alert.alert_id;

export function AlertsPanel({ panelApi }: AlertsPanelProps = {}): React.ReactElement {
  // Mounting the feed here seeds + refreshes the shared store while the
  // panel is open. The query state drives the first-load / error UI; the
  // rows always render from the store so a failed *refresh* keeps the
  // last-good list visible rather than blanking it.
  const feed = useRecentAlertsFeed();
  // ``useActiveAlerts`` applies the active-only filter (and newest-first); the
  // operator sort below is applied AFTER that filter (REQ-UI-110 — dismissed
  // alerts are never re-introduced by sorting).
  const alerts = useActiveAlerts();
  // ``producer_id → display_name`` join for the row label + the platform_name
  // sort (REQ-UI-110). Falls back to the id for an unresolved producer.
  const names = useProducerNames();
  // A row left-click inspects the alert (``inspected`` slot only) — Entity
  // Details + this list's highlight move, the Map ring stays put (the
  // 2026-06-09 two-slot redesign). A second left-click on the row that is
  // already inspected deselects it — both slots clear, the camera stays
  // (REQ-UI-051 amended 2026-10-01).
  const toggleInspect = useSelectionStore((s) => s.toggleInspect);
  const selectedAlertId = useInspectedIdForKind("alert");
  // The map slot too, only so an alert selected on the map is admitted to a
  // paused list and therefore has a row to reveal (REQ-UI-051).
  const mapSelectedAlertId = useMapSelectedIdForKind("alert");
  const canDismiss = useSession().hasAnyRole(ALERT_DISMISSER_ROLES);
  const listRef = useRef<HTMLUListElement | null>(null);

  // The operator's per-panel sort selection (REQ-UI-110), persisted in the
  // working-state / Profile ``list_sort``; absent ⇒ the panel default (time
  // descending, newest first). The header gear writes it; this body reads the
  // SAME working state so the list re-orders live.
  const sortSelection = usePreferences().workingState.list_sort?.alerts ?? ALERT_DEFAULT_SORT;

  // Sort a COPY of the active set (sortBy is non-mutating) by the selection,
  // AFTER the active-only filter. Recomputed only when the active set, the name
  // index, or the selection changes. Sorting is on row identity, so it never
  // disturbs the id-keyed selection or the keep-in-view anchor (REQ-UI-051).
  const sortedAlerts = useMemo(
    () => sortAlertRows(alerts, names, sortSelection),
    [alerts, names, sortSelection],
  );

  // What the list renders: the live sorted rows while following, the rows
  // frozen at the flip while not (REQ-UI-182 amended 2026-10-10; see
  // ``use-paused-list-rows``). An alert selected anywhere is admitted so it
  // still has a row while paused.
  const follow = useListFollow("alerts");
  const { rows: visibleAlerts, withheld } = usePausedListRows({
    rows: sortedAlerts,
    follow,
    idOf: alertRowId,
    admit: [selectedAlertId, mapSelectedAlertId],
  });

  // Keep the selected row visible: centre it on selection / when the tab is
  // shown, and hold it steady as alerts stream in. Keyed on the RENDERED list
  // so the anchor logic sees the order the operator sees (REQ-UI-051;
  // ADR-024 §5).
  useKeepSelectedRowInView(listRef, selectedAlertId, visibleAlerts, panelApi, { follow });

  if (feed.isLoading && alerts.length === 0) {
    return <LoadingState />;
  }
  if (feed.isError && alerts.length === 0) {
    return <ErrorState message={feed.error.message} onRetry={() => void feed.refetch()} />;
  }
  // Gated on the rendered rows: a list paused while empty stays empty.
  if (visibleAlerts.length === 0) {
    return <EmptyState />;
  }

  return (
    <div
      data-testid="alerts-panel"
      role="region"
      aria-label="Active alerts"
      className="flex h-full w-full flex-col bg-bg-base"
    >
      <header className="flex items-center justify-between gap-2 border-b border-border px-4 py-3">
        <h2 className="shrink-0 whitespace-nowrap text-body font-medium text-text-primary">
          Alerts <span className="ml-2 text-body-sm text-text-muted">{visibleAlerts.length}</span>
        </h2>
        <ListFollowToggle panelId="alerts" withheld={withheld} />
      </header>
      <ul
        ref={listRef}
        // ``overscroll-contain``: a wheel that reaches this list's end must not
        // continue into dockview's own ``.dv-view`` — an ``overflow-y: auto``
        // wrapper around the whole group, tab strip included — which scrolls the
        // panel's tab up under the top nav bar while the page itself never moves
        // (measured on Selection Details 2026-10-10: ``.dv-view`` scrollTop
        // 0 → 605, group y 48 → -557). No list panel can reach that state today
        // (``.dv-view`` has no scrollable extent around one — probed in both
        // directions at two viewport heights), but the end of a list is now a
        // place operators sit: a paused list stops moving, so they scroll to the
        // bottom and keep going (REQ-UI-182 amended 2026-10-10).
        className="flex-1 overflow-y-auto overscroll-contain"
        role="list"
        aria-label="Alert list"
      >
        {visibleAlerts.map((alert) => (
          <AlertRow
            key={alert.alert_id}
            alert={alert}
            platformName={names.nameFor(alert.producer_id)}
            canDismiss={canDismiss}
            onSelect={(id) => toggleInspect({ kind: "alert", id })}
          />
        ))}
      </ul>
    </div>
  );
}

interface AlertRowProps {
  alert: AlertRecord;
  /**
   * The source platform's display name (REQ-UI-110) — joined from the producers
   * inventory, or the raw ``producer_id`` when unresolved. Shown in place of the
   * raw id (which was the pre-REQ-UI-110 row content).
   */
  platformName: string;
  canDismiss: boolean;
  onSelect: (alertId: string) => void;
}

function AlertRow({
  alert,
  platformName,
  canDismiss,
  onSelect,
}: AlertRowProps): React.ReactElement {
  const isSelected = useIsInspected("alert", alert.alert_id);
  const priority = describeAlertPriority(alert.priority);
  const priorityColor = getStatusColor(priority.markerStatus);
  const typeLabel = alertTypeLabel(alert.alert_type);
  const description = alert.description ?? "";
  // An alert with no resolvable location (REQ-UI-069) renders no map glyph,
  // so "Select on Map" has nothing to recenter to → the item is disabled.
  const locatable = alert.latitude_deg !== null && alert.longitude_deg !== null;

  return (
    <SelectOnMapContextMenu value={{ kind: "alert", id: alert.alert_id }} locatable={locatable}>
      <li
        tabIndex={0}
        role="listitem"
        aria-selected={isSelected}
        aria-label={`${priority.label} priority ${typeLabel} alert from ${platformName}, ${formatAlertAge(alert.received_at)} old`}
        data-row-id={alert.alert_id}
        data-testid={`alert-row-${alert.alert_id}`}
        onClick={() => onSelect(alert.alert_id)}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            onSelect(alert.alert_id);
          }
        }}
        className={[
          "group relative cursor-pointer border-b border-border px-4 py-3",
          "transition-colors duration-100",
          "hover:bg-bg-elevated focus:outline-none focus-visible:bg-bg-elevated",
          isSelected ? "bg-bg-elevated" : "",
        ].join(" ")}
      >
        <span
          aria-hidden="true"
          className={[
            "absolute left-0 top-0 h-full w-[3px] transition-colors duration-100",
            isSelected ? "bg-brand" : "bg-transparent",
          ].join(" ")}
        />
        <div className="flex flex-col gap-1">
          <div className="flex items-center justify-between gap-2">
            {/* Priority badge — coloured pill + text label; colour is not
             * the sole signal, per REQ-UI-046. The swatch comes from the
             * shared status palette (no invented hex). */}
            <span
              className="inline-flex items-center gap-1.5 rounded px-1.5 py-0.5 text-body-sm font-medium"
              style={{
                color: priorityColor,
                backgroundColor: `color-mix(in srgb, ${priorityColor} 16%, transparent)`,
              }}
              data-testid="alert-row-priority"
            >
              <span
                aria-hidden="true"
                className="inline-block h-2 w-2 rounded-full"
                style={{ backgroundColor: priorityColor }}
              />
              {priority.label}
            </span>
            <span
              className="font-mono text-body-sm text-text-secondary"
              data-testid="alert-row-age"
            >
              {formatAlertAge(alert.received_at)}
            </span>
          </div>
          <span className="text-body-sm text-text-muted" data-testid="alert-row-type">
            {typeLabel}
          </span>
          <span
            className="line-clamp-2 text-body text-text-primary"
            data-testid="alert-row-description"
          >
            {description || "(no description)"}
          </span>
          <div className="flex items-center justify-between gap-2">
            <div className="flex min-w-0 items-center gap-2">
              {/* Source-platform DISPLAY NAME (REQ-UI-110) — joined from the
               * producers inventory; falls back to the raw producer_id when the
               * inventory hasn't (yet) resolved it. ``title`` carries the raw id
               * for log cross-reference. */}
              <span
                className="truncate font-mono text-body-sm text-text-muted"
                data-testid="alert-row-producer"
                title={alert.producer_id}
              >
                {platformName}
              </span>
              {/* Short, glanceable alert-id handle (ALT-…) as a row identifier —
               * the rows carried no id before; the full ULID rides ``title`` so
               * it stays log-cross-referenceable (REQ-UI-062; CLAUDE.md §8.1).
               * aria-hidden: the row's aria-label already names the alert. */}
              <span
                className="shrink-0 font-mono text-caption text-text-muted"
                data-testid="alert-row-id"
                aria-hidden="true"
                title={alert.alert_id}
              >
                {shortId(alert.alert_id, "ALT")}
              </span>
            </div>
            {canDismiss ? <DismissButton alertId={alert.alert_id} /> : null}
          </div>
        </div>
      </li>
    </SelectOnMapContextMenu>
  );
}

interface DismissButtonProps {
  alertId: string;
}

/**
 * The per-row Dismiss control (operator/admin only — the parent gates
 * rendering). Fires the dismiss mutation and stops click propagation so
 * dismissing doesn't also select the row. While pending it disables +
 * shows "Dismissing…"; on failure it surfaces an inline message (a 403
 * reads as a permission error per :func:`useDismissAlert`).
 */
function DismissButton({ alertId }: DismissButtonProps): React.ReactElement {
  const dismiss = useDismissAlert();
  return (
    <span className="flex items-center gap-2">
      {dismiss.isError ? (
        <span
          role="alert"
          className="text-body-sm text-destructive"
          data-testid={`alert-row-dismiss-error-${alertId}`}
        >
          {dismiss.error.status === 403 ? "Not permitted" : "Dismiss failed"}
        </span>
      ) : null}
      <button
        type="button"
        disabled={dismiss.isPending}
        aria-label="Dismiss alert"
        data-testid={`alert-row-dismiss-${alertId}`}
        onClick={(e) => {
          // Don't let the click bubble to the row (which would select it).
          e.stopPropagation();
          dismiss.mutate({ alertId });
        }}
        onKeyDown={(e) => {
          // Keep keyboard activation on the button from reaching the row.
          e.stopPropagation();
        }}
        className={[
          "rounded border border-border bg-bg-surface px-2 py-0.5 text-body-sm text-text-primary",
          "hover:bg-bg-elevated focus:outline-none focus-visible:bg-bg-elevated",
          "disabled:cursor-not-allowed disabled:opacity-60",
        ].join(" ")}
      >
        {dismiss.isPending ? "Dismissing…" : "Dismiss"}
      </button>
    </span>
  );
}

// ---- Render-state stubs ------------------------------------------

function LoadingState(): React.ReactElement {
  return (
    <div
      data-testid="alerts-panel-loading"
      role="status"
      aria-label="Loading alerts"
      className="flex h-full w-full items-center justify-center bg-bg-base text-text-secondary"
    >
      <p className="text-body-sm">Loading alerts…</p>
    </div>
  );
}

function EmptyState(): React.ReactElement {
  return (
    <div
      data-testid="alerts-panel-empty"
      role="region"
      aria-label="No active alerts"
      className="flex h-full w-full flex-col items-center justify-center gap-2 bg-bg-base text-text-secondary"
    >
      {/* Storyboard Flow F §"Empty state". */}
      <p className="text-body text-text-primary">No active alerts</p>
      <p className="text-body-sm text-text-muted">New alerts appear here as they arrive.</p>
    </div>
  );
}

function ErrorState({
  message,
  onRetry,
}: {
  message: string;
  onRetry: () => void;
}): React.ReactElement {
  return (
    <div
      data-testid="alerts-panel-error"
      role="alert"
      className="flex h-full w-full flex-col items-center justify-center gap-3 bg-bg-base"
    >
      <p className="text-body text-destructive">Could not load alerts</p>
      <p className="px-6 text-center text-body-sm text-text-secondary">{message}</p>
      <button
        type="button"
        onClick={onRetry}
        className="rounded border border-border bg-bg-surface px-3 py-1 text-body-sm text-text-primary hover:bg-bg-elevated"
      >
        Retry
      </button>
    </div>
  );
}
